'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

interface NavigationSection {
  id: string;
  label: string;
  href: string;
}

interface StickyNavigatorProps {
  sections?: NavigationSection[];
}

const StickyNavigator = ({ sections }: StickyNavigatorProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const defaultSections: NavigationSection[] = [
    { id: 'solution', label: 'Solution', href: '#solution' },
    { id: 'process', label: 'Process', href: '#process' },
    { id: 'results', label: 'Results', href: '#results' },
    { id: 'faq', label: 'FAQ', href: '#faq' },
  ];

  const navigationSections = sections || defaultSections;

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const heroSection = document.getElementById('hero');
      const heroHeight = heroSection ? heroSection.offsetHeight : 600;

      if (currentScrollY > heroHeight) {
        if (currentScrollY < lastScrollY) {
          setIsVisible(true);
        } else if (currentScrollY > lastScrollY + 50) {
          setIsVisible(false);
          setIsMobileDrawerOpen(false);
        }
      } else {
        setIsVisible(false);
      }

      lastScrollY = currentScrollY;

      const scrollPosition = currentScrollY + 150;
      const sectionIds = navigationSections.map(section => section.id);

      for (const sectionId of sectionIds) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(sectionId);
            break;
          }
        }
      }

      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        window.requestAnimationFrame(handleScroll);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick);
    return () => window.removeEventListener('scroll', requestTick);
  }, [navigationSections]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      const headerOffset = 140;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }

    setIsMobileDrawerOpen(false);
  };

  const toggleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  return (
    <>
      <div data-component-id="src/components/common/StickyNavigator.tsx:107:6" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="107" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`fixed top-20 left-0 right-0 z-[90] bg-background border-b border-border shadow-sm transition-transform duration-300 ${
          isVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div data-component-id="src/components/common/StickyNavigator.tsx:112:8" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="112" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%22%7D" className="max-w-7xl mx-auto px-6">
          <div data-component-id="src/components/common/StickyNavigator.tsx:113:10" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="113" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20md%3Aflex%20items-center%20justify-center%20space-x-2%20py-3%22%7D" className="hidden md:flex items-center justify-center space-x-2 py-3">
            {navigationSections.map((section) => (
              <Link data-component-id="src/components/common/StickyNavigator.tsx:115:14" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="115" data-component-file="StickyNavigator.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                key={section.id}
                href={section.href}
                onClick={(e) => handleNavClick(e, section.href)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeSection === section.id
                    ? 'bg-primary text-primary-foreground shadow-cta'
                    : 'text-text-primary hover:bg-secondary hover:text-primary'
                }`}
              >
                {section.label}
              </Link>
            ))}
          </div>

          <div data-component-id="src/components/common/StickyNavigator.tsx:130:10" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="130" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22md%3Ahidden%20flex%20items-center%20justify-between%20py-3%22%7D" className="md:hidden flex items-center justify-between py-3">
            <span data-component-id="src/components/common/StickyNavigator.tsx:131:12" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="131" data-component-file="StickyNavigator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-text-secondary%22%7D" className="text-sm font-medium text-text-secondary">
              {navigationSections.find(s => s.id === activeSection)?.label || 'Navigate'}
            </span>
            <button data-component-id="src/components/common/StickyNavigator.tsx:134:12" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="134" data-component-file="StickyNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20bg-secondary%20text-text-primary%20rounded-md%20text-sm%20font-medium%20hover%3Abg-muted%20transition-colors%20duration-200%22%2C%22textContent%22%3A%22Sections%22%7D"
              onClick={toggleMobileDrawer}
              className="px-4 py-2 bg-secondary text-text-primary rounded-md text-sm font-medium hover:bg-muted transition-colors duration-200"
            >
              Sections
            </button>
          </div>
        </div>
      </div>

      {isMobileDrawerOpen && (
        <div data-component-id="src/components/common/StickyNavigator.tsx:145:8" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="145" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-%5B250%5D%20md%3Ahidden%22%7D" className="fixed inset-0 z-[250] md:hidden">
          <div data-component-id="src/components/common/StickyNavigator.tsx:146:10" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="146" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%20bg-opacity-50%22%7D"
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={toggleMobileDrawer}
          />
          <div data-component-id="src/components/common/StickyNavigator.tsx:150:10" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="150" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-0%20left-0%20right-0%20bg-background%20rounded-t-2xl%20shadow-xl%20max-h-%5B70vh%5D%20overflow-y-auto%22%7D" className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl shadow-xl max-h-[70vh] overflow-y-auto">
            <div data-component-id="src/components/common/StickyNavigator.tsx:151:12" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="151" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20px-6%20py-4%20border-b%20border-border%22%7D" className="flex items-center justify-between px-6 py-4 border-b border-border">
              <span data-component-id="src/components/common/StickyNavigator.tsx:152:14" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="152" data-component-file="StickyNavigator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-text-primary%22%2C%22textContent%22%3A%22Jump%20to%20Section%22%7D" className="text-lg font-semibold text-text-primary">Jump to Section</span>
              <button data-component-id="src/components/common/StickyNavigator.tsx:153:14" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="153" data-component-file="StickyNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-text-secondary%20hover%3Atext-text-primary%20transition-colors%20duration-200%22%2C%22textContent%22%3A%22%E2%9C%95%22%7D"
                onClick={toggleMobileDrawer}
                className="text-text-secondary hover:text-text-primary transition-colors duration-200"
                aria-label="Close drawer"
              >
                ✕
              </button>
            </div>

            <div data-component-id="src/components/common/StickyNavigator.tsx:162:12" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="162" data-component-file="StickyNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20p-4%20space-y-2%22%7D" className="flex flex-col p-4 space-y-2">
              {navigationSections.map((section) => (
                <Link data-component-id="src/components/common/StickyNavigator.tsx:164:16" data-component-path="src/components/common/StickyNavigator.tsx" data-component-line="164" data-component-file="StickyNavigator.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                  key={section.id}
                  href={section.href}
                  onClick={(e) => handleNavClick(e, section.href)}
                  className={`px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                    activeSection === section.id
                      ? 'bg-primary text-primary-foreground shadow-cta'
                      : 'text-text-primary hover:bg-secondary'
                  }`}
                >
                  {section.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StickyNavigator;